import { NgModule, inject } from '@angular/core';
import { Routes, RouterModule, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { RoleGuard } from './core/guards/role.guard';
import { TurmaGuard } from './core/guards/turma.guard';
import { redirectIfAuthenticated } from './core/guards/redirect-if-authenticated.guard';
import { oportunidadeConvitePublicoCanMatch } from './core/guards/oportunidade-convite.guard';
import { PlatformService } from 'src/app/shared/services/platform.service';
import { AcessoShellComponent } from './features/acesso/components/acesso-shell/acesso-shell.component';

// Telas carregadas sob demanda (loadComponent): agenda, calculadora, Chama
// Smart (que traz o modal completo do card, ~1,5 MB), marketing e revisão da
// extensão ficavam no bundle inicial por serem importadas aqui com
// `component:`. O login (AcessoShellComponent) continua eager de propósito:
// é leve e é a primeira tela de quem está deslogado — lazy só somaria uma
// requisição em série antes de pintar o formulário.
const loadCalculadoraHome = () =>
  import('./features/calculadora/components/home/calculadora.component').then((m) => m.CalculadoraHomeComponent);

export const routes: Routes = [
  {
    path: 'inicio',
    canActivate: [(route: ActivatedRouteSnapshot) => {
      const platform = inject(PlatformService);
      if (platform.isNativeApp) {
        // Preservar queryParams: sem isso o redirectUrl do AuthInterceptor
        // se perdia e o login nativo sempre caía na home.
        return inject(Router).createUrlTree(['auth/login'], { queryParams: route.queryParams });
      }
      return redirectIfAuthenticated(route);
    }],
    loadComponent: () => import('./public/marketing/marketing.component').then((m) => m.MarketingComponent),
    title: 'Smart Leilões',
  },
  {
    path: 'extension/card-review',
    loadComponent: () =>
      import('./features/extension-card-review/components/extension-card-review.component').then((m) => m.ExtensionCardReviewComponent),
  },
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        component: AcessoShellComponent,
        data: { tab: 'login' },
        canActivate: [redirectIfAuthenticated],
      },
      {
        path: 'forgot-my-password',
        component: AcessoShellComponent,
        data: { tab: 'forgot' },
      },
      {
        path: 'register',
        component: AcessoShellComponent,
        data: { tab: 'register' },
      },
    ]
  },
  {
    path: 'precos',
    loadComponent: () =>
      import('./public/pricing/pricing.component').then(
        (m) => m.PricingComponent,
      ),
    title: 'Planos e Preços — Smart Leilões',
  },
  {
    // Checkout de assinatura (Stripe Custom Checkout) — serve logado E anônimo,
    // por isso fica fora do shell autenticado (sem AuthGuard).
    path: 'checkout',
    loadComponent: () =>
      import('./public/checkout/checkout-page.component').then(
        (m) => m.CheckoutPageComponent,
      ),
    title: 'Finalizar assinatura — Smart Leilões',
  },
  {
    path: 'blog',
    loadComponent: () =>
      import('./public/blog/blog-list/blog-list.component').then(
        (m) => m.BlogListComponent,
      ),
    title: 'Blog — Smart Leiloes',
  },
  {
    path: 'blog/:slug',
    loadComponent: () =>
      import('./public/blog/blog-post/blog-post.component').then(
        (m) => m.BlogPostComponent,
      ),
  },
  {
    // Descadastro de e-mails pelo link no rodapé (release notes/informativos).
    // Pública e sem AuthGuard: o usuário clica direto da caixa de entrada, sem
    // sessão; a identidade vem do HMAC nos query params `e`/`t`.
    path: 'descadastro',
    loadComponent: () =>
      import('./public/descadastro/descadastro.component').then(
        (m) => m.DescadastroComponent,
      ),
    title: 'Descadastrar e-mails — Smart Leilões',
  },
  {
    path: 'public',
    loadChildren: () => import('./public/public.module').then(m => m.PublicModule)
  },
  {
    path: 'imersao-renda-10x',
    loadComponent: () =>
      import('./public/imersao-renda-10x/imersao-renda-10x.component').then(
        (m) => m.ImersaoRenda10xComponent,
      ),
    title: 'Imersão Renda 10X — Liberar acesso ao Smart App',
  },
  {
    path: 'imoveis-pop',
    loadComponent: () =>
      import('./public/imoveis-pop/imoveis-pop.component').then(
        (m) => m.ImoveisPopComponent,
      ),
    title: 'Imersão Escalando com Imóveis POP — Liberar acesso ao Smart App',
  },
  {
    path: 'lucrando-com-imoveis-pop/confirmar',
    loadComponent: () =>
      import('./public/lucrando-imoveis-pop/confirmar/lucrando-imoveis-pop-confirmar.component').then(
        (m) => m.LucrandoImoveisPopConfirmarComponent,
      ),
    title: 'Confirmar e-mail — Lucrando com Imóveis POP',
  },
  {
    path: 'lucrando-com-imoveis-pop',
    loadComponent: () =>
      import('./public/lucrando-imoveis-pop/lucrando-imoveis-pop.component').then(
        (m) => m.LucrandoImoveisPopComponent,
      ),
    title: 'Lucrando com Imóveis POP — Criar conta no Smart App',
  },
  {
    path: 'imersao-arena-pop',
    loadComponent: () =>
      import('./public/imersao-arena-pop/imersao-arena-pop.component').then(
        (m) => m.ImersaoArenaPopComponent,
      ),
    title: 'Imersão Arena POP — Liberar acesso ao Smart App',
  },
  {
    path: 'imersao-tubaroes',
    loadComponent: () =>
      import('./public/imersao-tubaroes/imersao-tubaroes.component').then(
        (m) => m.ImersaoTubaroesComponent,
      ),
    title: 'Imersão dos Tubarões — Liberar acesso ao Smart App',
  },
  {
    path: 'imobclub',
    loadComponent: () =>
      import('./public/imobclub/imobclub.component').then(
        (m) => m.ImobClubComponent,
      ),
    title: 'Inauguração ImobClub — Liberar acesso ao Smart App',
  },
  {
    path: 'assessoria-lucrativa',
    loadComponent: () =>
      import('./public/assessoria-lucrativa/assessoria-lucrativa.component').then(
        (m) => m.AssessoriaLucrativaComponent,
      ),
    title: 'Imersão Assessoria Lucrativa — Liberar acesso ao Smart App',
  },
  {
    path: 'seu-1-milhao',
    loadComponent: () =>
      import('./public/seu-1-milhao/seu-1-milhao.component').then(
        (m) => m.Seu1MilhaoComponent,
      ),
    title: 'Workshop Seu 1º Milhão — Liberar acesso ao Smart App',
  },
  {
    path: 'fabrica-de-milionarios',
    loadComponent: () =>
      import('./public/fabrica-de-milionarios/fabrica-de-milionarios.component').then(
        (m) => m.FabricaDeMilionariosComponent,
      ),
    title: 'A Fábrica de Milionários — Liberar acesso ao Smart App',
  },
  {
    // Convite público por link (design "Convite público"): a MESMA URL do detalhe
    // (`/oportunidades/:cardId`, a que o bot de WhatsApp divulga) casa aqui só
    // para visitante sem sessão; logado, a rota não casa e a URL segue para o
    // detalhe autenticado dentro da casca abaixo.
    path: 'oportunidades/:cardId',
    canMatch: [oportunidadeConvitePublicoCanMatch],
    title: 'Smart Leilões | Oportunidade para investidores',
    loadComponent: () =>
      import('./features/oportunidades-investidor/pages/oportunidade-convite/oportunidade-convite.page').then(
        (m) => m.OportunidadeConvitePage,
      ),
  },
  {
    path: '',
    canActivate: [AuthGuard],
    loadComponent: () => import('./features/features.component').then(m => m.FeaturesComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'home'
      },
      {
        // Home oficial (dashboard v2). O chrome (header/sidebar novos) vem do
        // FeaturesComponent, então esta rota é só o conteúdo do grid.
        path: 'home',
        pathMatch: 'full',
        title: 'Smart Leilões | Home',
        loadComponent: () =>
          import('./features/home/components/home-v2/home-v2.component').then(
            (m) => m.HomeV2Component,
          ),
        data: { animationDepth: 0 },
      },
      {
        path: 'agenda',
        pathMatch: 'full',
        title: 'Smart Leilões | Agenda',
        loadComponent: () => import('./features/agenda/agenda.component').then((m) => m.AgendaComponent),
        // Acessível a qualquer plano (decisão de produto 2026-08-07) — Gratuito
        // só tem até 3 cards no Gerenciamento de Imóveis, então a Agenda (que é
        // uma projeção desses cards) aparece naturalmente vazia/quase vazia
        // como teaser, sem precisar de gate de role.
        data: { animationDepth: 1 },
      },
      {
        path: 'imoveis',
        pathMatch: 'prefix',
        loadChildren: () =>
          import('./features/imoveis/imoveis.module').then((m) => m.ImoveisModule),
        data: { animationDepth: 1 },
      },
      {
        path: 'meus-imoveis',
        pathMatch: 'prefix',
        title: 'Smart Leilões | Meus Imóveis',
        loadChildren: () =>
          import('./features/meus-imoveis/meus-imoveis.module').then((m) => m.MeusImoveisModule),
        data: { animationDepth: 1 },
      },
      {
        path: 'radar',
        pathMatch: 'full',
        title: 'Smart Leilões | Radar de Imóveis',
        loadComponent: () =>
          import('./features/radar/radar-page.component').then((m) => m.RadarPageComponent),
        data: { animationDepth: 1 },
      },
      {
        path: 'notificacoes',
        pathMatch: 'prefix',
        title: 'Smart Leilões | Notificações',
        loadChildren: () =>
          import('./features/notificacoes/notificacoes.module').then( m => m.NotificacoesModule),
        data: { animationDepth: 1 },
      },
      {
        path: 'calculadora',
        pathMatch: 'prefix',
        title: 'Smart Leilões | Calculadora',
        loadComponent: loadCalculadoraHome,
        data: { animationDepth: 1 },
      },
      {
        path: 'calculadora/home',
        loadComponent: loadCalculadoraHome,
        data: { animationDepth: 1 },
      },
      {
        path: 'calculadora/simulacoes',
        loadComponent: () =>
          import('./features/calculadora/components/calculadora-simulacoes/calculadora-simulacoes.component').then(
            (m) => m.CalculadoraSimulacoesComponent,
          ),
        data: { animationDepth: 2 },
      },
      {
        path: 'perfil',
        pathMatch: 'prefix',
        title: 'Smart Leilões | Meu Usuário',
        loadChildren: () =>
          import('./features/perfil/perfil.module').then((m) => m.PerfilModule),
        data: { animationDepth: 1 },
      },
      {
        path: 'chama-smart',
        pathMatch: 'full',
        loadComponent: () => import('./features/chama-smart/components/chama-smart.component').then((m) => m.ChamaSmartComponent),
        data: { animationDepth: 1 },
      },
      {
        path: 'suporte',
        pathMatch: 'prefix',
        title: 'Smart Leilões | Suporte',
        loadChildren: () =>
          import('./features/suporte/suporte.module').then( m => m.SuporteModule),
        data: { animationDepth: 1 },
      },
      {
        path: 'administrar',
        title: 'Smart Leilões | Administrar',
        loadChildren: () =>
          import('./features/administrar/administrar.module').then( m => m.AdministrarModule),
        data: { animationDepth: 1 },
      },
      {
        path: 'flip-link',
        pathMatch: 'prefix',
        title: 'Smart Leilões | FlipLink',
        loadChildren: () => import('./features/smart-link/smart-link.module').then(m => m.SmartLinkModule),
        data: { animationDepth: 1 },
      },
      // Compatibilidade: links antigos (notificações, e-mails) usavam /smart-link
      {
        path: 'smart-link/gerenciamento-imoveis',
        redirectTo: 'flip-link/gerenciamento-imoveis',
      },
      {
        path: 'smart-link/gestao-imoveis',
        redirectTo: 'flip-link/gestao-imoveis',
      },
      {
        path: 'gestao-financeira',
        pathMatch: 'full',
        title: 'Smart Leilões | Gestão Financeira',
        loadComponent: () => import('./features/gestao-financeira/components/gestao-financeira/gestao-financeira.component').then(m => m.GestaoFinanceiraComponent),
        data: { animationDepth: 1 },
      },
      {
        path: 'gestao-financeira/novo-lancamento',
        pathMatch: 'full',
        title: 'Smart Leilões | Novo Lançamento',
        loadComponent: () => import('./features/gestao-financeira/components/novo-lancamento-page/novo-lancamento-page.component').then(m => m.NovoLancamentoPageComponent),
        data: { animationDepth: 2 },
      },
      {
        path: 'gestao-financeira/detalhes-transacao',
        pathMatch: 'full',
        title: 'Smart Leilões | Detalhes da Transação',
        loadComponent: () => import('./features/gestao-financeira/components/detalhes-transacao-page/detalhes-transacao-page.component').then(m => m.DetalhesTransacaoPageComponent),
        data: { animationDepth: 2 },
      },
      {
        path: 'gerenciar-imoveis/oportunidade-tubaroes/:cardId',
        title: 'Smart Leilões | Oportunidade Tubarões',
        loadComponent: () => import('./features/gerenciar-imoveis/components/card-tubarao-view/card-tubarao-view.component').then(m => m.CardTubaraoViewComponent),
        data: { animationDepth: 2 },
      },
      {
        path: 'gerenciar-imoveis',
        pathMatch: 'full',
        title: 'Smart Leilões | Gerenciar Imóveis',
        loadComponent: () => import('./features/gerenciar-imoveis/components/gerenciar-imoveis.component').then(m => m.GerenciarImoveisComponent),
        data: { animationDepth: 1 },
      },
      {
        // Camada do investidor: banco de oportunidades, detalhe e "minhas".
        // Aberta a qualquer usuário autenticado (sem RoleGuard).
        path: 'oportunidades',
        loadChildren: () =>
          import('./features/oportunidades-investidor/oportunidades-investidor.routes').then(
            (m) => m.OPORTUNIDADES_INVESTIDOR_ROUTES,
          ),
      },
      {
        path: 'admin-smart-link',
        title: 'Smart Leilões | Administrador FlipLink',
        canActivate: [RoleGuard],
        data: { role: 'ADMIN_SMART_LINK', animationDepth: 1 },
        loadChildren: () =>
          import('./features/admin-smart-link-v2/admin-smart-link-v2.routes').then(
            (m) => m.ADMIN_SMART_LINK_V2_ROUTES
          ),
      },
      {
        path: 'admin-smart-link-v2',
        redirectTo: 'admin-smart-link',
      },
      {
        // Mapa dos Smartanos foi aposentado e substituído pelo ImobNegócios (mapa de
        // parceiros). Rota redireciona para preservar links antigos.
        path: 'mapa-smartanos',
        redirectTo: 'imob-negocios',
        pathMatch: 'full',
      },
      {
        // ImobClub é exclusivo de alunos do treinamento Smart (mesma regra que gatava
        // o antigo Mapa dos Smartanos): exige `acquisitionData.turma`. O TurmaGuard
        // redireciona quem não participa de nenhuma turma para a Home. Os pontos de
        // entrada (home v2 + shell) ainda mostram o item com cadeado, então o guard é
        // a defesa para acesso direto pela URL.
        path: 'imob-negocios',
        canActivate: [TurmaGuard],
        loadChildren: () => import('./features/imob-negocios/imob-negocios.routes').then((m) => m.IMOB_NEGOCIOS_ROUTES),
        data: { animationDepth: 1 },
      },
      {
        path: '**',
        redirectTo: 'home',
      }
    ]
  },
  {
    path: '**',
    canActivate: [() => {
      const platformService = inject(PlatformService);
      const router = inject(Router);
      return router.createUrlTree([platformService.isNativeApp ? 'auth/login' : 'inicio']);
    }],
    children: [],
  }
];
